// Live code highlighter — mirrors Strudel's active-code pattern for bytebeat.
// Parses the expression with Lezer (JavaScript CST) to find branch points
// (ternaries, short-circuit logic), bit-level features (shift amounts),
// operators to glow by value, and number literals to pulse.
//
// Wire: call updateCode(src) on editor change, tick(t) from the play loop.

import { javascriptLanguage } from '@codemirror/lang-javascript';

// 0 → cool blue, 128 → green, 255 → warm red
function byteToColor(v) {
	const h = 240 - (v / 255) * 240; // 240° blue → 0° red
	const s = 75;
	const l = 55 + (v / 255) * 10;   // brighter at high values
	return `hsl(${h.toFixed(0)},${s}%,${l}%)`;
}

export class Highlighter {
	constructor() {
		this.ternary = true;
		this.shortCircuit = true;
		this.bitShift = true;
		this.operatorGlow = true;
		this.branches = [];   // branch conditions → bitmask
		this._vals = [];      // operator / number value expressions
		this._evalFn = null;
		this._lastState = null;
	}

	get enabled() {
		return this.ternary || this.shortCircuit || this.bitShift || this.operatorGlow;
	}

	updateCode(code) {
		this.branches = [];
		this._vals = [];
		this._evalFn = null;
		this._lastState = null;
		if (!code || code.length < 3) return false;

		let topNode;
		try { topNode = javascriptLanguage.parser.parse(code).topNode; }
		catch (_) { return false; }

		this._collectCST(topNode, code);
		if (this.branches.length === 0 && this._vals.length === 0) return false;

		this._buildEvalFn();
		return true;
	}

	tick(t) {
		if (!this.enabled || !this._evalFn) return null;
		let result;
		try { result = this._evalFn(t); }
		catch (_) { return null; }
		if (!result) return null;

		const mask = result[0] | 0;
		// Only emit when state changed (branches OR values shifted)
		if (mask === this._lastState && this._vals.length === 0) return null;
		this._lastState = mask;

		const ranges = [];
		let vi = 1; // value index (after mask at [0])

		for (const b of this.branches) {
			const active = !!(mask & (1 << b._bi));
			if (b.kind === 'ternary' && this.ternary) {
				ranges.push({
					from: active ? b.trueFrom : b.falseFrom,
					to:   active ? b.trueTo   : b.falseTo,
					color: active ? '#4ec05a' : '#6fb0ff',
					render: 'stripe', // diagonal hash — structural, distinct from operator glow
				});
			} else if (b.kind === 'and' && this.shortCircuit) {
				if (active) ranges.push({ from: b.trueFrom, to: b.trueTo, color: '#4ec05a', render: 'stripe' });
			} else if (b.kind === 'or' && this.shortCircuit) {
				if (!active) ranges.push({ from: b.trueFrom, to: b.trueTo, color: '#6fb0ff', render: 'stripe' });
			} else if (b.kind === 'shift' && this.bitShift) {
				ranges.push({
					from: b.bitFrom, to: b.bitTo,
					color: active ? '#ffd28a' : '#3a414d',
					render: 'pulse', // subtle gold — rhythmic
				});
			}
		}

		// Operator glow + number pulse: values[vi..] are 0-255 byte values
		if (this.operatorGlow) {
			for (const v of this._vals) {
				const bv = result[vi++] | 0;
				if (v.from >= v.to) continue;
				ranges.push({
					from: v.from, to: v.to,
					color: byteToColor(bv),
					alpha: 0.10 + (bv / 255) * 0.12,
					render: 'glow', // flat fill — subtle background
				});
			}
		}

		return ranges.length ? ranges : null;
	}

	reset() { this._lastState = null; }

	// ── CST walk ───────────────────────────────────────────────────────

	_collectCST(node, code) {
		if (node.name === 'ConditionalExpression') this._collectTernary(node, code);
		if (node.name === 'BinaryExpression') this._collectBinary(node, code);
		if (node.name === 'Number') this._collectNumber(node, code);
		for (let c = node.firstChild; c; c = c.nextSibling) this._collectCST(c, code);
	}

	_collectTernary(node, code) {
		const exprs = [];
		for (let c = node.firstChild; c; c = c.nextSibling) {
			const txt = code.slice(c.from, c.to);
			if (txt === '?' || txt === ':') continue;
			exprs.push(c);
		}
		if (exprs.length < 3) return;
		const condCode = code.slice(exprs[0].from, exprs[0].to);
		if (!condCode) return;
		this.branches.push({
			kind: 'ternary', condCode, _bi: this.branches.length,
			trueFrom: exprs[1].from, trueTo: exprs[1].to,
			falseFrom: exprs[2].from, falseTo: exprs[2].to,
		});
	}

	_collectBinary(node, code) {
		let left = null, op = null, right = null, opText = '';
		for (let c = node.firstChild; c; c = c.nextSibling) {
			const txt = code.slice(c.from, c.to);
			if (c.name === 'LogicOp' || c.name === 'BitOp' || c.name === 'ArithOp') {
				op = c; opText = txt;
			} else if (!left) left = c;
			else right = c;
		}
		if (!left || !right) return;

		// Short-circuit logic
		if (opText === '&&' || opText === '||') {
			const condCode = code.slice(left.from, left.to);
			if (!condCode) return;
			this.branches.push({
				kind: opText === '&&' ? 'and' : 'or', condCode,
				_bi: this.branches.length,
				trueFrom: right.from, trueTo: right.to,
				falseFrom: 0, falseTo: 0,
			});
		}

		// Bit shift pulse
		if ((opText === '>>' || opText === '>>>') && left.name === 'VariableName'
			&& code.slice(left.from, left.to) === 't') {
			const N = parseInt(code.slice(right.from, right.to), 10);
			if (!isNaN(N) && N >= 0 && N < 32) {
				this.branches.push({
					kind: 'shift', condCode: `(t>>${N})&1`,
					_bi: this.branches.length,
					bitFrom: right.from, bitTo: right.to,
					trueFrom: 0, trueTo: 0, falseFrom: 0, falseTo: 0,
				});
			}
		}

		// Operator glow — color the operator token by the expression's byte value
		if (op && this.operatorGlow) {
			const exprCode = code.slice(node.from, node.to);
			if (exprCode && exprCode.length < 200) {
				this._vals.push({ from: op.from, to: op.to, _code: exprCode });
			}
		}
	}

	_collectNumber(node, code) {
		// Pulse numeric literals by their parent expression's activity
		// Walk up to the nearest BinaryExpression parent (not possible in
		// a pure recursive walk, so just pulse the number itself by its
		// constant value — gives a static warm/cool tint based on magnitude)
		if (!this.operatorGlow) return;
		const numCode = code.slice(node.from, node.to);
		// Use the number as its own expression — constant values produce
		// a steady color tint showing hot (large) vs cool (small) literals
		if (numCode && node.from < node.to) {
			this._vals.push({ from: node.from, to: node.to, _code: `(${numCode})>>>0` });
		}
	}

	// ── Eval ───────────────────────────────────────────────────────────

	_buildEvalFn() {
		const params = Object.getOwnPropertyNames(Math);
		const values = params.map(k => Math[k]);
		params.push('int', 'window');
		values.push(Math.floor, globalThis);

		const parts = [];
		// Branch conditions → bitmask
		const maskParts = this.branches.map((b, i) =>
			`((${b.condCode}) ? ${1 << i} : 0)`);
		parts.push(maskParts.join(' | ') || '0');

		// Operator / number value expressions → byte values
		for (const v of this._vals) {
			parts.push(`((${v._code})>>>0)&255`);
		}

		const body = `return [${parts.join(', ')}];`;
		try {
			this._evalFn = new Function(...params, 't', body)
				.bind(globalThis, ...values);
		} catch (_) {
			this._evalFn = null;
		}
	}
}
